// Third Party
import React from 'react';
import cx from 'classnames';

// Utils
import { QuoteItemizedType } from '@gql';
import { formatWholeDollarAmount } from '@utils/money';

// UI
import { Typography } from '@components/typography';

export type IQuoteBreakdownProps = {
  numberOfNights: number;
  totalWithoutTaxes: number;
  quoteBreakdownItems: {
    costCents: number;
    type: QuoteItemizedType;
    description: string;
  }[];
};

export const ListingQuoteBreakdown: React.FC<IQuoteBreakdownProps> = ({
  numberOfNights,
  totalWithoutTaxes,
  quoteBreakdownItems,
}) => {
  // Function to get the order of the types
  const getOrder = (type: QuoteItemizedType) => {
    switch (type) {
      case QuoteItemizedType.Base:
        return 1;
      case QuoteItemizedType.Fee:
        return 2;
      case QuoteItemizedType.Other:
        return 3;
      case QuoteItemizedType.Discount:
        return 4;
      default:
        return 5;
    }
  };

  // Sorting the items based on the type order
  const quoteItems = [...quoteBreakdownItems].sort((a, b) => {
    return getOrder(a.type) - getOrder(b.type);
  });

  const categorizedQuoteItems = quoteItems.reduce(
    (acc, item) => {
      if (!acc[item.type]) {
        acc[item.type] = [];
      }
      acc[item.type].push(item);
      return acc;
    },
    {} as Record<QuoteItemizedType, typeof quoteItems>
  );

  const feeCents = categorizedQuoteItems[
    QuoteItemizedType.Fee || QuoteItemizedType.Other
  ]?.reduce((acc, item) => acc + item.costCents, 0);

  return (
    <div className="relative space-y-2 pt-2">
      {quoteItems?.length === 0 && (
        <div className="flex w-full justify-between space-x-2">
          <Typography component="span" variant="body2" className="font-medium">
            {formatWholeDollarAmount(
              Math.round(totalWithoutTaxes / numberOfNights)
            )}{' '}
            x {numberOfNights} nights
          </Typography>

          <Typography component="span" variant="body2" className="font-medium">
            {formatWholeDollarAmount(totalWithoutTaxes)}
          </Typography>
        </div>
      )}

      {categorizedQuoteItems[QuoteItemizedType.Base]?.length > 0 && (
        <div className="flex w-full justify-between space-x-2">
          <Typography component="span" variant="body2" className="font-medium">
            {formatWholeDollarAmount(
              Math.round(
                (categorizedQuoteItems[QuoteItemizedType.Base][0].costCents +
                  (feeCents || 0)) /
                  numberOfNights
              )
            )}{' '}
            x {numberOfNights} nights
          </Typography>
          <Typography component="span" variant="body2" className="font-medium">
            {formatWholeDollarAmount(
              categorizedQuoteItems[QuoteItemizedType.Base][0].costCents +
                (feeCents || 0)
            )}
          </Typography>
        </div>
      )}

      {categorizedQuoteItems[QuoteItemizedType.Discount]?.map(
        (quoteItem, index) => (
          <div key={index}>
            <PrimaryBreakdown quoteBreakdownItem={quoteItem} />
          </div>
        )
      )}

      <div className="border-b border-primary-dark pb-2" />

      <div className="flex w-full justify-between space-x-2 pt-2">
        <Typography component="span" variant="subtitle1" className="font-bold">
          Total before taxes
        </Typography>

        <Typography component="span" variant="subtitle1" className="font-bold">
          {formatWholeDollarAmount(totalWithoutTaxes)}
        </Typography>
      </div>
    </div>
  );
};

export type QuoteBreakdownItemProps = {
  quoteBreakdownItem: {
    costCents: number;
    type: QuoteItemizedType;
    description: string;
  };
};

export const PrimaryBreakdown: React.FC<QuoteBreakdownItemProps> = ({
  quoteBreakdownItem,
}) => {
  return (
    <div
      className={cx('flex w-full justify-between space-x-2', {
        'text-secondary-dark font-bold':
          quoteBreakdownItem.type === QuoteItemizedType.Discount,
      })}
    >
      <Typography component="span" variant="subtitle2">
        {quoteBreakdownItem.description}
      </Typography>
      <Typography component="span" variant="body2" className="font-medium">
        {formatWholeDollarAmount(quoteBreakdownItem.costCents)}
      </Typography>
    </div>
  );
};
